import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import sumBy from 'lodash/sumBy';
import { ReducerStatus, RootState } from '../types';
import { MyBookingSessionModal, MyBookingSessionState } from './types';
import { ProductType, SeatAvailability, SeatingProduct } from '@/api/Cart/cart.interfaces';
import {
    AvailableAddonType,
    AnyUpsellAddon,
    UpSellAddonBaggage,
    UpSellAddonSeating,
    UpSellAddonServicePackage,
    UpSellAddonFerry,
    UpSellAddonTravelInsurance,
} from '@/api/MyBookingSession/myBookingSession.interfaces';
import { getSelectedBaggages } from '@shared/helpers/baggage';
import { Money } from '@libs/money/money.interfaces';
import { mapAvailableAddons } from '@/store/myBookingSession/helpers';
import { trackClarity } from '@/tracking/clarity';
import { ClarityEvent } from '@/tracking/clarity/types';
import { Widget } from '@/store/myBookingSession/Widget.interfaces';
import { UpSellAddonItem } from '@/components/myBookingPage/Sections/UpSell/UpSell.interfaces';

const initialState: MyBookingSessionState = {
    status: ReducerStatus.INITIAL,
    data: { AvailableAddons: [] },
    modal: null,
    widgets: [],
    upSells: [],
};

const state: MyBookingSessionState = {
    ...initialState,
};

const getters: GetterTree<MyBookingSessionState, RootState> = {
    getAvailableAddons(state): AnyUpsellAddon[] {
        return state?.data?.AvailableAddons ?? [];
    },
    getSeatingAddons: (state, getter, rootState, rootGetter): UpSellAddonSeating[] => {
        const seatingAddons = getter.getAvailableAddons.filter(
            ({ Type }: AnyUpsellAddon) => Type === AvailableAddonType.Seating
        ) as UpSellAddonSeating[];
        return seatingAddons.filter((addon) =>
            rootGetter['myBooking/getFlightProductByPnr'](addon.Pnr)
        );
    },

    getServicePackageAddons: (state, getter): UpSellAddonServicePackage[] => {
        return (
            getter.getAvailableAddons.filter(
                ({ Type }: AnyUpsellAddon) => Type === AvailableAddonType.ServicePackage
            ) as UpSellAddonServicePackage[]
        ).filter((a) => !!a.PackageType);
    },

    getTravelInsuranceAddon: (state, getter): UpSellAddonTravelInsurance | undefined => {
        return getter.getAvailableAddons.find(
            ({ Type }: AnyUpsellAddon) => Type === AvailableAddonType.TravelInsurance
        );
    },

    getFerryAddons: (state, getter): UpSellAddonFerry | undefined => {
        return getter.getAvailableAddons.filter(
            ({ ProductType: productType }: AnyUpsellAddon) =>
                productType === ProductType.Ferry
        );
    },

    getBaggageAddons: (state, getter, rootState, rootGetter): UpSellAddonBaggage[] => {
        const baggageAddons = getter.getAvailableAddons.filter(
            ({ Type }: AnyUpsellAddon) => Type === AvailableAddonType.Baggage
        ) as UpSellAddonBaggage[];
        return baggageAddons.filter((addon) =>
            rootGetter['myBooking/getFlightProductByPnr'](addon.Pnr)
        );
    },

    getSeatingTotalPrice(state: MyBookingSessionState, getter): Money {
        const seatingSum = getter.getSeatingAddons.flatMap(
            (addon: UpSellAddonSeating) => addon.SelectedSeats ?? []
        );

        return {
            Amount: sumBy(seatingSum, 'Price.Amount'),
            Currency: seatingSum[0]?.Currency ?? '',
        };
    },

    getBaggageTotalPrice(state: MyBookingSessionState, getter): Money {
        const baggageSum = getter.getBaggageAddons.flatMap((addon: UpSellAddonBaggage) =>
            addon?.Pax.flatMap((p) => getSelectedBaggages(p.AdditionalBaggage))
        );

        return {
            Amount: sumBy(baggageSum, 'Price.Amount'),
            Currency: baggageSum[0]?.Currency ?? '',
        };
    },

    getModal(state) {
        return state?.modal;
    },

    getWidgets(state) {
        return state.widgets;
    },

    getUpSells(state) {
        return state.upSells;
    },

    getWidgetById: (_, getter) => (id: string) =>
        getter.getWidgets.find((widget: Widget) => widget.id === id),

    getUpSellByType: (_, getter) => (type: string) =>
        getter.getUpSells.find((upSell: UpSellAddonItem) => upSell.type === type),
};

const actions: ActionTree<MyBookingSessionState, RootState> = {
    setStatusLoading({ state, commit }) {
        commit('setStatus', {
            ...state,
            status: ReducerStatus.LOADING,
        });
    },

    setStatusUpdating({ state, commit }) {
        commit('setStatus', {
            ...state,
            status: ReducerStatus.UPDATING,
        });
    },

    setStatusSuccess({ commit }, payload: { AvailableAddons: AnyUpsellAddon[] }) {
        const { AvailableAddons = [] } = payload;
        commit('setStatus', {
            ...state,
            status: ReducerStatus.LOADED,
            data: {
                AvailableAddons: mapAvailableAddons(AvailableAddons),
            },
        });
    },

    setStatusError({ state, commit }) {
        trackClarity(ClarityEvent.MY_BOOKING_SESSION_INITIAL_ERROR);

        commit('setStatus', {
            ...state,
            status: ReducerStatus.ERROR,
            data: null,
        });
    },

    setModal({ state, commit }, payload) {
        commit('setModal', {
            ...state,
            modal: payload,
        });
    },

    setAddonStatus(
        { state, commit },
        payload: { addon: AnyUpsellAddon; status: ReducerStatus }
    ) {
        commit('updateAvailableAddons', {
            availableAddons: state.data.AvailableAddons.map((addon) =>
                addon.Id === payload.addon.Id
                    ? {
                          ...addon,
                          Status: payload.status,
                      }
                    : addon
            ),
        });
    },

    updateSeating(
        { state, commit },
        payload: { addon: UpSellAddonSeating; seats: SeatingProduct[] }
    ) {
        commit('updateAvailableAddons', {
            availableAddons: state.data.AvailableAddons.map((addon) =>
                addon.Type === payload.addon.Type && addon?.Pnr === payload.addon?.Pnr
                    ? {
                          ...addon,
                          SelectedSeats: payload.seats,
                      }
                    : addon
            ),
        });
    },

    resetSeatingAddons({ state, commit }) {
        commit('updateAvailableAddons', {
            availableAddons: state.data.AvailableAddons.map((addon) =>
                addon.Type === AvailableAddonType.Seating
                    ? {
                          ...addon,
                          SelectedSeats: [],
                      }
                    : addon
            ),
        });
    },

    updateSeatAvailability(
        { state, commit },
        payload: {
            addon: UpSellAddonSeating;
            seatAvailability: SeatAvailability;
        }
    ) {
        commit('updateAvailableAddons', {
            availableAddons: state.data.AvailableAddons.map((addon) =>
                addon.Type === AvailableAddonType.Seating &&
                addon.Id === payload.addon.Id &&
                addon?.GordianSession.TripId === payload.addon?.GordianSession.TripId
                    ? {
                          ...addon,
                          GordianSession: {
                              ...addon.GordianSession,
                              SeatAvailability: payload.seatAvailability,
                          },
                      }
                    : addon
            ),
        });
    },

    updatePax({ state, commit }, payload: { addon: UpSellAddonBaggage }) {
        commit('updateAvailableAddons', {
            availableAddons: state.data.AvailableAddons.map((addon) =>
                addon.Type === payload.addon.Type && addon.Id === payload.addon.Id
                    ? {
                          ...addon,
                          InitialPax: addon.InitialPax ?? addon.Pax,
                          Pax: payload.addon.Pax,
                      }
                    : addon
            ),
        });
    },

    resetBaggageAddons({ state, commit }) {
        commit('updateAvailableAddons', {
            availableAddons: state.data.AvailableAddons.map((addon) =>
                addon.Type === AvailableAddonType.Baggage
                    ? {
                          ...addon,
                          Pax: addon?.InitialPax ?? addon.Pax,
                      }
                    : addon
            ),
        });
    },

    clearState({ commit }) {
        commit('clearState', initialState);
    },

    addWidget({ state, commit }, widget) {
        commit('updateWidgets', {
            widgets: state.widgets.concat(widget),
        });
    },

    updateWidget({ state, commit }, widget) {
        commit('updateWidgets', {
            widgets: state.widgets.map((w) => (w.id === widget.id ? { ...widget } : w)),
        });
    },
};

const mutations: MutationTree<MyBookingSessionState> = {
    setStatus(state: MyBookingSessionState, payload) {
        state.status = payload.status;
        state.data = payload.data;
    },
    setModal(state: MyBookingSessionState, payload: MyBookingSessionModal) {
        state.modal = payload;
    },
    updateAvailableAddons(state: MyBookingSessionState, payload) {
        state.data.AvailableAddons = payload.availableAddons;
    },
    clearState(state: MyBookingSessionState, payload) {
        Object.assign(state, payload);
    },
    updateWidgets(state: MyBookingSessionState, payload) {
        state.widgets = payload.widgets;
    },
    addUpSells(state: MyBookingSessionState, payload) {
        state.upSells = payload;
    },
};

export const myBookingSession: Module<MyBookingSessionState, RootState> = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
